/**
 * SOLOMON — LEAD CALCULATOR (Phase 1.5 §4A — line-item MAO model)
 *
 * Embedded inside LeadDetail. Operator-facing offer calculator with
 * per-column fee levers, shared pct assumptions (commission/closing/profit),
 * condition-driven rehab defaults, three-column MAO output
 * (conservative / target / walk-away), and per-lead persistence to
 * leads.calculator_state JSONB.
 *
 * Tier 2 partial: ARV confidence pill (sourced from valuation
 *   confidence proxy). Comps display deferred per KNOWN_ISSUES Issue 4.
 *
 * Persistence: debounced PATCH /api/leads/:id/calculator. Live MAO
 * computed client-side via computeBreakdown (mirrors backend formula);
 * server persists the canonical calculator_state for downstream readers.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { formatCurrency, formatRelativeTime } from './leadsConstants';
import CostSubtitle from '../CostSubtitle.jsx';
import ConfirmCostDialog from '../ConfirmCostDialog.jsx';
import { useCostMap } from '../../hooks/useCostMap.js';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Saved-state debounce. Long enough that a fast typist isn't hammering
// the server, short enough that "did my edit save?" feedback is quick.
const SAVE_DEBOUNCE_MS = 600;

const CONDITION_OPTIONS = [
  // Order matters — drives display sequence in the dropdown
  { key: 'excellent',  label: 'Excellent (cosmetic)' },
  { key: 'good',       label: 'Good (light)' },
  { key: 'fair',       label: 'Fair (standard)' },
  { key: 'poor',       label: 'Poor (heavy)' },
  { key: 'distressed', label: 'Distressed (full gut)' },
];

// Display-only mirror of calculatorService.computeLineItemMao — keeps the
// breakdown instant on a live call. Server-persisted calculator_state is the
// source of truth; this MUST match the backend formula's rounding.
function computeBreakdown(arv, { commissionPct, closingPct, profitPct, rehab, fee }) {
  const a = Number(arv);
  if (!Number.isFinite(a) || a <= 0) return null;
  const commissions = Math.round(a * (Number(commissionPct) || 0));
  const closing     = Math.round(a * (Number(closingPct) || 0));
  const profit      = Math.round(a * (Number(profitPct) || 0));
  const r = Number(rehab) || 0;
  const f = Number(fee)   || 0;
  const mao = Math.max(0, Math.round(a - commissions - closing - profit - r - f));
  return { arv: a, commissions, closing, profit, rehab: r, fee: f, mao };
}
const DEFAULT_PCTS = { commission: 0.06, closing: 0.01, profit: 0.15 };
const DEFAULT_FEES = { conservative: 20000, target: 10000, walk_away: 5000 };
const pctInput = (d) => +(Number(d) * 100).toFixed(2);   // 0.06 -> 6
const pctParse = (v) => (Number(v) || 0) / 100;          // "6" -> 0.06
const pctLabel = (d) => `${+(Number(d) * 100).toFixed(2)}%`;

export default function LeadCalculator({ leadId, lead }) {
  const { authFetch } = useAuth();
  const { costMap, threshold } = useCostMap();
  const [refreshArvDialogOpen, setRefreshArvDialogOpen] = useState(false);
  const [pullCompsDialogOpen, setPullCompsDialogOpen] = useState(false);

  // ── Form state ──────────────────────────────────────────────
  // ARV pre-fill priority: operator-saved value > Solomon-estimated ARV >
  // empty. Operator edits persist to calculator_state.arv on save.
  const initialState = lead?.calculator_state || null;
  const initialArv =
    initialState?.arv ??
    (lead?.estimated_arv != null ? Number(lead.estimated_arv) : '');
  const [arv,        setArv]        = useState(initialArv);
  const [refreshingArv, setRefreshingArv] = useState(false);
  const [arvRefreshError, setArvRefreshError] = useState(null);
  // Comp aggregate (Move 1 — BatchData MCP `comparable_property_count` cache).
  // Fetched on mount + after manual Pull Comps button. Display-only state;
  // backend handles auto-fire on connected_interested disposition.
  const [compAggregate, setCompAggregate] = useState(null);
  const [compRadius, setCompRadius]       = useState(0.25);
  const [pullingComps, setPullingComps]   = useState(false);
  const [compPullError, setCompPullError] = useState(null);
  // Comp records (Move 2 — BatchData MCP `comparable_property_preview` cache).
  // Holds the per-comp records + post-filter usability summary. `fallback=true`
  // (usable_count<3) drives the LOUD warning state that the operator on a live
  // call cannot miss. Single Pull Comps button refreshes both aggregate +
  // records in parallel; this state mirrors the records half.
  const [compRecords, setCompRecords] = useState(null);
  const [recordsExpanded, setRecordsExpanded] = useState(false);
  // Owned public-records comps (cost cutover 2026-06-12) — free, default
  // comp surface; BatchData pull is the paid escape hatch.
  const [ownedComps, setOwnedComps] = useState(null);
  const [ownedExpanded, setOwnedExpanded] = useState(false);
  const [condition,  setCondition]  = useState(initialState?.condition ?? '');
  const [rehab,      setRehab]      = useState(initialState?.rehab ?? '');
  const [commissionPct, setCommissionPct] = useState(initialState?.commission_pct ?? DEFAULT_PCTS.commission);
  const [closingPct,    setClosingPct]    = useState(initialState?.closing_pct ?? DEFAULT_PCTS.closing);
  const [profitPct,     setProfitPct]     = useState(initialState?.profit_pct ?? DEFAULT_PCTS.profit);
  const [fees,          setFees]          = useState(initialState?.fees ?? { ...DEFAULT_FEES });
  const [configDefaults, setConfigDefaults] = useState(null);

  // ── Server-derived data ─────────────────────────────────────
  const [conditionDefaults, setConditionDefaults] = useState(null);
  const [sqftFactors, setSqftFactors] = useState(null); // $/sqft rehab model (Issue 5 retune)
  const [savingStatus,   setSavingStatus]       = useState(null); // 'saving' | 'saved' | 'error'
  const [saveError,      setSaveError]          = useState(null);
  const [lastEditedAt,   setLastEditedAt]       = useState(initialState?.last_edited_at || null);

  const saveTimer = useRef(null);
  const initialLoadDone = useRef(false);

  // ── Load condition defaults from server ──────────────────────
  // The server owns the condition rehab ranges and shared pct defaults.
  // UI fetches them once on mount; ARV is still passed so the server can
  // return any ARV-scoped config in the future.
  const fetchConfig = useCallback(async (currentArv) => {
    const qs = currentArv ? `?arv=${encodeURIComponent(currentArv)}` : '';
    try {
      const res = await authFetch(`${API_BASE}/api/leads/calculator/config${qs}`);
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      setConditionDefaults(data.data.condition_defaults);
      setSqftFactors(data.data.condition_sqft_factors || null);
      setConfigDefaults({
        commission_pct: data.data.commission_pct,
        closing_pct: data.data.closing_pct,
        profit_pct: data.data.profit_pct,
        fee_triplet: data.data.fee_triplet,
      });
    } catch (err) {
      console.warn('[Calculator] config fetch failed:', err.message);
    }
  }, [authFetch]);

  // Debounce config fetch so it doesn't fire on every ARV keystroke.
  // Previously: typing "175000" = 6 keystrokes = 6 GETs to /calculator/config,
  // which combined with the save-loop cascade burst past the 300-req/15min
  // global limiter. Now: one fetch after the user pauses typing.
  useEffect(() => {
    const t = setTimeout(() => fetchConfig(arv ? Number(arv) : null), 400);
    return () => clearTimeout(t);
  }, [fetchConfig, arv]);

  // ── Condition change → pre-fill rehab with default ──────────
  // $/sqft model when lead sqft is known (midpoint × sqft, Issue 5
  // retune); flat legacy default otherwise. Mirrors the server-side
  // getRehabDefaultForCondition chain so rehab_default_used derives true.
  const onConditionChange = (newCondition) => {
    setCondition(newCondition);
    if (!newCondition) return;
    const leadSqft = lead?.sqft != null ? Number(lead.sqft) : null;
    if (sqftFactors?.[newCondition] && leadSqft > 0) {
      setRehab(String(Math.round(sqftFactors[newCondition].mid * leadSqft)));
    } else if (conditionDefaults?.[newCondition]) {
      setRehab(String(conditionDefaults[newCondition].default_rehab));
    }
  };

  // ── Fee + pct handlers ───────────────────────────────────────
  const onFeeChange = (key, value) => {
    setFees((prev) => ({ ...prev, [key]: value === '' ? '' : Number(value) }));
  };
  const resetToDefaults = () => {
    const d = configDefaults || { commission_pct: DEFAULT_PCTS.commission, closing_pct: DEFAULT_PCTS.closing, profit_pct: DEFAULT_PCTS.profit, fee_triplet: { ...DEFAULT_FEES } };
    setCommissionPct(d.commission_pct);
    setClosingPct(d.closing_pct);
    setProfitPct(d.profit_pct);
    setFees({ ...d.fee_triplet });
  };

  // ── Save (debounced) ────────────────────────────────────────
  const triggerSave = useCallback(async () => {
    // Avoid saving incomplete state — need at least ARV or rehab
    // (both numeric-coercible) to be meaningful.
    const arvNum   = arv === '' ? null : Number(arv);
    const rehabNum = rehab === '' ? 0 : Number(rehab);
    if (arv !== '' && (!Number.isFinite(arvNum) || arvNum < 0)) return;
    if (!Number.isFinite(rehabNum) || rehabNum < 0) return;

    setSavingStatus('saving');
    setSaveError(null);
    try {
      const body = {
        arv:            arvNum,
        condition:      condition || null,
        rehab:          rehabNum,
        commission_pct: Number(commissionPct),
        closing_pct:    Number(closingPct),
        profit_pct:     Number(profitPct),
        fees: {
          conservative: Number(fees.conservative) || 0,
          target:       Number(fees.target) || 0,
          walk_away:    Number(fees.walk_away) || 0,
        },
      };
      const res = await authFetch(`${API_BASE}/api/leads/${leadId}/calculator`, {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      setLastEditedAt(data.data.state?.last_edited_at || new Date().toISOString());
      setSavingStatus('saved');
      setTimeout(() => setSavingStatus((s) => s === 'saved' ? null : s), 1500);
    } catch (err) {
      setSavingStatus('error');
      setSaveError(err.message || 'Save failed');
    }
  }, [authFetch, leadId, arv, condition, rehab, commissionPct, closingPct, profitPct, fees]);

  useEffect(() => {
    if (!initialLoadDone.current) { initialLoadDone.current = true; return; }
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(triggerSave, SAVE_DEBOUNCE_MS);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [arv, condition, rehab, commissionPct, closingPct, profitPct, fees, triggerSave]);

  // ── Confidence pill ──────────────────────────────────────────
  const confidencePill = useMemo(() => {
    const conf = lead?.arv_confidence;
    if (!conf) return null;
    const map = { low: 'low', medium: 'med', high: 'high' };
    return { label: map[conf] || conf, level: conf };
  }, [lead]);

  // ── Render ──────────────────────────────────────────────────
  const conditionDef = condition ? conditionDefaults?.[condition] : null;
  const arvNumber = arv === '' ? null : Number(arv);

  // Solomon-derived ARV info from the lead row. Distinct from `arv`
  // which is what's CURRENTLY in the input (might be operator-edited).
  const solomonArv      = lead?.estimated_arv != null ? Number(lead.estimated_arv) : null;
  const solomonArvLow   = lead?.arv_low  != null ? Number(lead.arv_low)  : null;
  const solomonArvHigh  = lead?.arv_high != null ? Number(lead.arv_high) : null;
  const priceRangeLow   = lead?.price_range_low  != null ? Number(lead.price_range_low)  : null;
  const priceRangeHigh  = lead?.price_range_high != null ? Number(lead.price_range_high) : null;

  // Is the current input ARV the Solomon-derived value (no operator edit yet)?
  const isUsingSolomonArv =
    solomonArv != null &&
    arv !== '' &&
    Number(arv) === solomonArv;

  const refreshArv = async () => {
    setRefreshingArv(true);
    setArvRefreshError(null);
    try {
      const res = await authFetch(`${API_BASE}/api/leads/${leadId}/arv-refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ forceRecompute: true }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      window.dispatchEvent(new CustomEvent('solomon:reload-lead', { detail: { leadId } }));
      if (isUsingSolomonArv || arv === '') {
        setArv(data.data.estimated_arv);
      }
    } catch (err) {
      setArvRefreshError(err.message);
    } finally {
      setRefreshingArv(false);
    }
  };

  // Load cached comp aggregate on mount + when leadId changes. Cache-hit is
  // free (no MCP call); if nothing's cached, returns null and we show the
  // empty state until operator clicks Pull Comps (or auto-fire on
  // connected_interested populates the cache server-side).
  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;
    (async () => {
      try {
        const [aggRes, recRes, ownedRes] = await Promise.all([
          authFetch(`${API_BASE}/api/leads/${leadId}/comp-aggregate`),
          authFetch(`${API_BASE}/api/leads/${leadId}/comp-records`),
          authFetch(`${API_BASE}/api/leads/${leadId}/owned-comps`),
        ]);
        const [aggData, recData, ownedData] = await Promise.all([aggRes.json(), recRes.json(), ownedRes.json()]);
        if (cancelled) return;
        if (aggData.success) setCompAggregate(aggData.data || null);
        if (recData.success) setCompRecords(recData.data || null);
        if (ownedData.success) setOwnedComps(ownedData.data || null);
      } catch (err) {
        if (!cancelled) console.warn('[Calculator] comp read failed:', err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [authFetch, leadId]);

  const pullComps = async () => {
    setPullingComps(true);
    setCompPullError(null);
    try {
      // Fire both pulls in parallel — aggregate ($1.35) + records (~$1.35) ≈ $2.70 total when both halves uncached. See 03-Operations/Vendors/batchdata-button-cost-map-2026-05-27.
      // Operator pays once for both anchors. Cache TTL 7 days on each
      // independently so a stale-records refetch doesn't re-bill aggregate
      // and vice versa.
      const [aggPull, recPull] = await Promise.all([
        authFetch(`${API_BASE}/api/leads/${leadId}/comp-pull`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ radius_miles: compRadius, forceRefresh: true }),
        }),
        authFetch(`${API_BASE}/api/leads/${leadId}/comp-records-pull`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ radius_miles: compRadius, forceRefresh: true }),
        }),
      ]);
      const aggData = await aggPull.json();
      const recData = await recPull.json();
      if (!aggPull.ok || !aggData.success) throw new Error(aggData.error || `aggregate HTTP ${aggPull.status}`);
      if (!recPull.ok || !recData.success) throw new Error(recData.error || `records HTTP ${recPull.status}`);
      // Re-read normalized cached shapes
      const [r2, r3] = await Promise.all([
        authFetch(`${API_BASE}/api/leads/${leadId}/comp-aggregate`),
        authFetch(`${API_BASE}/api/leads/${leadId}/comp-records`),
      ]);
      const [d2, d3] = await Promise.all([r2.json(), r3.json()]);
      if (d2.success) setCompAggregate(d2.data || null);
      if (d3.success) setCompRecords(d3.data || null);
    } catch (err) {
      setCompPullError(err.message);
    } finally {
      setPullingComps(false);
    }
  };

  // Owned public-records derivations — the free default comp surface.
  // ≥3 owned comps = the same threshold the ARV anchor uses.
  const ownedPool = ownedComps?.supported === true ? (ownedComps.comps || []) : [];
  const ownedAgg = ownedComps?.supported === true ? ownedComps.aggregate : null;
  const hasOwnedAnchor = ownedPool.length >= 3;
  const ownedTier = (ownedAgg?.total_count ?? 0) >= 50 ? 'high'
                  : (ownedAgg?.total_count ?? 0) >= 20 ? 'medium'
                  : 'low';
  const ownedSubjectAnchor = (ownedAgg?.avg_price_per_sqft != null && Number.isFinite(Number(lead?.sqft)))
    ? ownedAgg.avg_price_per_sqft * Number(lead.sqft)
    : null;

  const compConfTier = compAggregate?.confidence?.tier;
  const compShouldDisplay = compConfTier && compConfTier !== 'unknown';
  const compSubjectAnchor = (compAggregate?.avg_price_per_sqft != null && Number.isFinite(Number(lead?.sqft)))
    ? compAggregate.avg_price_per_sqft * Number(lead.sqft)
    : null;

  // Move 2 record-tier derivation — drives the loud-fallback chip + accordion.
  // fallback === true when usable_count < 3 (post-filter for sqft tol / sold
  // age / property-type / disclosed-price). UI surfaces this prominently so
  // an operator on a live call instantly knows whether neighbor-comp defense
  // is available before saying "houses around yours sold for $X".
  const recordsTier   = compRecords?.confidence_tier || null;
  const isFallback    = compRecords?.fallback === true;
  const usableRecords = (compRecords?.records || []).filter((r) => r.usable);
  const otherRecords  = (compRecords?.records || []).filter((r) => !r.usable);
  // Plan A: chip annotation reflects sqft tolerance bands. Mixed-band pools
  // surface as "STRONG (5 tight + 3 wide)" so operator sees quantity + quality.
  // Single-band pools stay terse ("STRONG (8 comps)") — no annotation noise.
  const bb = compRecords?.band_breakdown || { tight: 0, wide: 0, widest: 0 };
  const bandSegments = [
    bb.tight  > 0 ? `${bb.tight} tight`   : null,
    bb.wide   > 0 ? `${bb.wide} wide`     : null,
    bb.widest > 0 ? `${bb.widest} widest` : null,
  ].filter(Boolean);
  const isMixedBand = bandSegments.length > 1;
  const usableCount = compRecords?.usable_count ?? 0;
  const chipCountText = isMixedBand ? bandSegments.join(' + ') : `${usableCount} comp${usableCount === 1 ? '' : 's'}`;
  const bandTooltip = isMixedBand
    ? `Sqft tolerance bands — tight: ≤±20%, wide: ±20-30%, widest: ±30-40%. Wider bands get larger sqft adjustments in the ARV math.`
    : null;
  const recordsChipLabel = recordsTier === 'strong'   ? `STRONG (${chipCountText})`
                         : recordsTier === 'adequate' ? `ADEQUATE (${chipCountText})`
                         : recordsTier === 'thin'     ? `THIN POOL (${chipCountText})`
                         : recordsTier === 'fallback' ? `NO USABLE COMPS — SYNTHESIS FALLBACK`
                         : null;

  return (
    <section className="lead-calc-section">
      <h3 className="lead-detail-h">
        Offer Calculator
        {savingStatus === 'saving' && <span className="lead-calc-status">saving…</span>}
        {savingStatus === 'saved'  && <span className="lead-calc-status saved">saved</span>}
        {savingStatus === 'error'  && <span className="lead-calc-status error">save failed</span>}
        {lastEditedAt && !savingStatus && (
          <span className="lead-calc-status">last edited {formatRelativeTime(lastEditedAt)}</span>
        )}
      </h3>

      {/* Option C 2-column split (2026-05-20):
          LEFT  ~45%: ARV input + Solomon badge + specs + AS-IS + action buttons
          RIGHT ~55%: Condition/Rehab/Fee inputs (top) + anchor panels (bottom)
          Anchor panels use flex-grow auto-fill so a single panel spans full
          right-col width; two panels split 50/50. Errors flow full-width
          below the split. MAO output (3-col) unchanged below. */}
      <div className="lead-calc-c-split">
        {/* LEFT — ARV context stack */}
        <div className="lead-calc-c-left">
          <div className="lead-calc-field">
            <label className="lead-calc-label">
              ARV (After-Repair Value)
              {confidencePill && (
                <span className={`lead-calc-confidence-pill conf-${confidencePill.level}`}>
                  {confidencePill.label}
                </span>
              )}
              {isFallback && (
                <span
                  className="lead-calc-fallback-tag"
                  title="No usable neighbor comps — ARV is from subject-only synthesis (AVM + sale history + building specs). No comp anchors available for this lead."
                >
                  synthesis only
                </span>
              )}
            </label>
            <input
              type="number"
              className="sol-input"
              placeholder={solomonArv ? `Solomon: $${solomonArv.toLocaleString()}` : 'Type ARV…'}
              value={arv}
              onChange={(e) => setArv(e.target.value)}
              min={0}
            />
            {isUsingSolomonArv && (
              <div className="lead-calc-arv-badge">
                <span className="dot" />
                Solomon tri-engine committee (Claude + GPT-4o + Gemini)
                {solomonArvLow != null && solomonArvHigh != null && (
                  <span className="lead-calc-arv-range">
                    range {formatCurrency(solomonArvLow)}–{formatCurrency(solomonArvHigh)}
                  </span>
                )}
              </div>
            )}
          </div>
          {/* Property specs from BatchData enrichment — Tax Assessor dataset */}
          {(lead?.sqft || lead?.bedrooms || lead?.bathrooms || lead?.year_built || lead?.building_condition) && (
            <div className="lead-calc-specs">
              {lead.sqft != null && <span className="spec"><strong>{Number(lead.sqft).toLocaleString()}</strong> sqft</span>}
              {lead.bedrooms != null && <span className="spec"><strong>{lead.bedrooms}</strong> bd</span>}
              {lead.bathrooms != null && <span className="spec"><strong>{Number(lead.bathrooms)}</strong> ba</span>}
              {lead.year_built != null && <span className="spec">built <strong>{lead.year_built}</strong>{lead.effective_year_built && lead.effective_year_built !== lead.year_built ? ` (eff ${lead.effective_year_built})` : ''}</span>}
              {lead.building_condition && <span className="spec spec--cond">cond: <strong>{lead.building_condition}</strong></span>}
              {lead.last_sale_price != null && (
                <span className="spec spec--sale">
                  last sold <strong>{formatCurrency(lead.last_sale_price)}</strong>
                  {lead.last_sale_date ? ` (${String(lead.last_sale_date).slice(0, 4)})` : ''}
                </span>
              )}
              {lead.assessor_market_value != null && (
                <span className="spec spec--assessor">
                  assessor <strong>{formatCurrency(lead.assessor_market_value)}</strong>
                </span>
              )}
            </div>
          )}
          {lead?.property_value_estimate != null && (
            <div className="lead-calc-reference">
              Current AS-IS estimate: <strong>{formatCurrency(lead.property_value_estimate)}</strong>
              {priceRangeLow != null && priceRangeHigh != null && (
                <> (range {formatCurrency(priceRangeLow)}–{formatCurrency(priceRangeHigh)})</>
              )}
            </div>
          )}
          <div className="lead-calc-arv-actions">
            <div className="button-with-cost">
              <button
                type="button"
                className="lead-calc-arv-refresh"
                onClick={() => {
                  const cost = costMap?.buttons?.refresh_arv?.cost_non_cached_usd_max ?? 0;
                  if (cost > (threshold ?? 2)) {
                    setRefreshArvDialogOpen(true);
                  } else {
                    refreshArv();
                  }
                }}
                disabled={refreshingArv}
                title="Run tri-engine ARV committee (Claude + GPT-4o + Gemini in parallel) over the lead's enriched BatchData. ~$0.06 inference only — no extra BatchData spend. Divergence > 5% surfaces a conflict chip."
              >
                {refreshingArv ? 'Recalculating…' : (solomonArv ? 'Recalculate ARV' : 'Estimate ARV')}
              </button>
              <CostSubtitle buttonId="refresh_arv" />
              <ConfirmCostDialog
                isOpen={refreshArvDialogOpen}
                buttonId="refresh_arv"
                leadId={lead.id}
                onConfirm={() => { setRefreshArvDialogOpen(false); refreshArv(); }}
                onCancel={() => setRefreshArvDialogOpen(false)}
              />
            </div>
            <select
              className="lead-calc-comp-radius"
              value={compRadius}
              onChange={(e) => setCompRadius(Number(e.target.value))}
              disabled={pullingComps}
              title="Search radius for nearby comp aggregate. 0.25mi (default) = same neighborhood section. 0.10mi = same block. 0.50mi = adjacent subdivisions."
            >
              <option value={0.10}>0.10 mi</option>
              <option value={0.25}>0.25 mi</option>
              <option value={0.50}>0.50 mi</option>
            </select>
            <div className="button-with-cost">
              <button
                type="button"
                className="lead-calc-comp-pull"
                onClick={() => {
                  const cost = costMap?.buttons?.pull_comps?.cost_non_cached_usd_max ?? 0;
                  if (cost > (threshold ?? 2)) {
                    setPullCompsDialogOpen(true);
                  } else {
                    pullComps();
                  }
                }}
                disabled={pullingComps}
                title={`Optional PAID refresh — owned public-records comps load free by default. Pulls the BatchData comp aggregate + comp records (~$2.70 non-cached, $0 if both halves cached within 7d). Filters: ±1 bed / ±20% sqft / ±15yr built, sold-only last 180 days.`}
              >
                {pullingComps ? 'Pulling…' : compAggregate ? 'Refresh BatchData Comps' : 'Pull BatchData Comps'}
              </button>
              <CostSubtitle buttonId="pull_comps" />
              <ConfirmCostDialog
                isOpen={pullCompsDialogOpen}
                buttonId="pull_comps"
                leadId={lead.id}
                onConfirm={() => { setPullCompsDialogOpen(false); pullComps(); }}
                onCancel={() => setPullCompsDialogOpen(false)}
              />
            </div>
          </div>
        </div>

        {/* RIGHT — operator inputs (top) + anchor panels (bottom) */}
        <div className="lead-calc-c-right">
          <div className="lead-calc-c-right-inputs">
            <div className="lead-calc-field">
              <label className="lead-calc-label">Condition</label>
              <select
                className="sol-input"
                value={condition}
                onChange={(e) => onConditionChange(e.target.value)}
              >
                <option value="">Select condition…</option>
                {CONDITION_OPTIONS.map(opt => (
                  <option key={opt.key} value={opt.key}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="lead-calc-field">
              <label className="lead-calc-label">Rehab</label>
              <input
                type="number"
                className="sol-input"
                placeholder="Rehab cost"
                value={rehab}
                onChange={(e) => setRehab(e.target.value)}
                min={0}
              />
              {conditionDef && (() => {
                const leadSqft = lead?.sqft != null ? Number(lead.sqft) : null;
                const f = condition ? sqftFactors?.[condition] : null;
                return (
                  <div className="lead-calc-hint">
                    {f && leadSqft > 0
                      ? `${formatCurrency(Math.round(f.low * leadSqft))}–${formatCurrency(Math.round(f.high * leadSqft))} typical for ${conditionDef.label.toLowerCase()} condition at $${f.low}–$${f.high}/sqft × ${leadSqft.toLocaleString()} sqft.`
                      : `${formatCurrency(conditionDef.range_low)}–${formatCurrency(conditionDef.range_high)} typical for ${conditionDef.label.toLowerCase()} condition (flat default — sqft unavailable).`}
                  </div>
                );
              })()}
            </div>
          </div>
          {/* Anchor panels — flex-grow so a single panel spans full width;
              two panels split 50/50. Container omitted entirely when both
              are absent (no phantom empty row). */}
          {(hasOwnedAnchor || isFallback || (compShouldDisplay && compAggregate?.error == null) || (lead?.arv_model_conflict === true && lead?.arv_model_outputs)) && (
          <div className="lead-calc-c-right-anchors">
            {/* OWNED PUBLIC-RECORDS panel — the default ($0) comp surface
                (cost cutover 2026-06-12). Same pool the ARV engine anchored
                on: county-recorded arm's-length sales, same zip, ±20% sqft,
                365d. BatchData panels below are the paid escape hatch. */}
            {hasOwnedAnchor && (
              <div className={`lead-calc-comp-aggregate conf-${ownedTier}`}>
                <div className="lead-calc-comp-aggregate-header">
                  <span className="lead-calc-comp-aggregate-label">Public records</span>
                  <span className={`lead-calc-comp-aggregate-chip conf-${ownedTier}`}>
                    {ownedTier === 'high' ? 'STRONG' : ownedTier === 'medium' ? 'MODERATE' : 'THIN'}
                  </span>
                  <span className="lead-calc-comp-aggregate-meta">
                    {ownedAgg?.total_count ?? ownedPool.length} recorded sales · same zip · {ownedComps?.filters?.sqft_band_pct ? `±${ownedComps.filters.sqft_band_pct}% sqft · ` : ''}arm's-length · 365d · $0
                  </span>
                </div>
                <div className="lead-calc-comp-aggregate-row">
                  {ownedAgg?.avg_price_per_sqft != null && (
                    <span className="comp-stat">
                      avg <strong>{formatCurrency(ownedAgg.avg_price_per_sqft)}/sqft</strong>
                    </span>
                  )}
                  {ownedAgg?.median_price != null && (
                    <span className="comp-stat">
                      median sale <strong>{formatCurrency(ownedAgg.median_price)}</strong>
                    </span>
                  )}
                  {ownedSubjectAnchor != null && (
                    <span className="comp-stat comp-stat--anchor">
                      subject anchor <strong>{formatCurrency(ownedSubjectAnchor)}</strong>
                    </span>
                  )}
                </div>
                <div className="lead-calc-comp-records-accordion">
                  <button
                    type="button"
                    className="lead-calc-comp-records-toggle"
                    onClick={() => setOwnedExpanded((v) => !v)}
                    aria-expanded={ownedExpanded}
                  >
                    {ownedExpanded ? '▾' : '▸'} {ownedExpanded ? 'Hide' : 'Show'} {ownedPool.length} recorded comp{ownedPool.length === 1 ? '' : 's'}
                  </button>
                  {ownedExpanded && (
                    <div className="lead-calc-comp-records-table">
                      <div className="lead-calc-comp-records-row lead-calc-comp-records-row--head">
                        <span>Address</span>
                        <span>Sold</span>
                        <span>Price</span>
                        <span>$/sqft</span>
                        <span>Sqft</span>
                        <span>Bd/Yr</span>
                      </div>
                      {ownedPool.map((c) => (
                        <div key={c.source_record_id} className="lead-calc-comp-records-row">
                          <span className="comp-cell-addr">{c.street}{c.city ? `, ${c.city}` : ''}</span>
                          <span>{c.sale_date ? String(c.sale_date).slice(0, 10) : '—'}</span>
                          <span>{c.sale_price != null ? formatCurrency(Number(c.sale_price) / 100) : '—'}</span>
                          <span>{c.sale_price != null && c.sqft ? `$${(Number(c.sale_price) / 100 / c.sqft).toFixed(0)}` : '—'}</span>
                          <span>{c.sqft != null ? Number(c.sqft).toLocaleString() : '—'}</span>
                          <span>{c.beds ?? '—'}bd / {c.year_built ?? '—'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* LOUD FALLBACK panel — when NEITHER the owned table NOR usable
                BatchData records exist. Amber warning, NOT red (this is honest
                data unavailability, not error). Operator on a live call must
                not miss this — no neighbor-comp defense available. */}
            {!hasOwnedAnchor && isFallback ? (
              <div className="lead-calc-comp-fallback" role="status">
                <div className="lead-calc-comp-fallback-header">
                  <span className="lead-calc-comp-fallback-icon" aria-hidden="true">⚠</span>
                  <strong>No usable comp anchors — falling back to enriched synthesis</strong>
                </div>
                <div className="lead-calc-comp-fallback-body">
                  ARV below is from subject's own data (AVM, sale history, building specs).
                  No neighbor-comp defense available for this lead.
                </div>
                <div className="lead-calc-comp-fallback-meta">
                  {compRecords?.total_recent_sales || 0} recent sales found in area · {compRecords?.with_price_count || 0} with disclosed prices · {compRecords?.usable_count || 0} usable after property-comparability filters (sqft / age / type). Below the 3-comp threshold for comp-anchored math.
                </div>
              </div>
            ) : compShouldDisplay && compAggregate?.error == null && (
              <div className={`lead-calc-comp-aggregate conf-${compConfTier}`}>
                <div className="lead-calc-comp-aggregate-header">
                  <span className="lead-calc-comp-aggregate-label">Nearby market (BatchData)</span>
                  <span className={`lead-calc-comp-aggregate-chip conf-${compConfTier}`}>
                    {compConfTier === 'high' ? 'STRONG' : compConfTier === 'medium' ? 'MODERATE' : compConfTier === 'low' ? 'THIN' : 'THIN POOL'}
                  </span>
                  {recordsChipLabel && recordsTier !== 'fallback' && (
                    <span
                      className={`lead-calc-comp-records-chip tier-${recordsTier}`}
                      title={bandTooltip || undefined}
                    >
                      {recordsChipLabel}
                    </span>
                  )}
                  <span className="lead-calc-comp-aggregate-meta">
                    {compAggregate.total_count} comps · {compAggregate.radius_miles}mi · ±1 bed / ±20% sqft / ±15yr · sold 180d
                  </span>
                </div>
                {compConfTier === 'thin' ? (
                  <div className="lead-calc-comp-aggregate-thin">
                    Only {compAggregate.total_count} comps at {compAggregate.radius_miles}mi — widen radius below to 0.5mi, or rely on synthesis ARV.
                  </div>
                ) : (
                  <div className="lead-calc-comp-aggregate-row">
                    {compAggregate.avg_price_per_sqft != null && (
                      <span className="comp-stat">
                        avg <strong>{formatCurrency(compAggregate.avg_price_per_sqft)}/sqft</strong>
                      </span>
                    )}
                    {compAggregate.avg_price != null && (
                      <span className="comp-stat">
                        avg sale <strong>{formatCurrency(compAggregate.avg_price)}</strong>
                      </span>
                    )}
                    {compSubjectAnchor != null && (
                      <span className="comp-stat comp-stat--anchor">
                        subject anchor <strong>{formatCurrency(compSubjectAnchor)}</strong>
                      </span>
                    )}
                  </div>
                )}
                {compRecords && usableRecords.length > 0 && (
                  <div className="lead-calc-comp-records-accordion">
                    <button
                      type="button"
                      className="lead-calc-comp-records-toggle"
                      onClick={() => setRecordsExpanded((v) => !v)}
                      aria-expanded={recordsExpanded}
                    >
                      {recordsExpanded ? '▾' : '▸'} {recordsExpanded ? 'Hide' : 'Show'} {usableRecords.length} usable comp{usableRecords.length === 1 ? '' : 's'}
                      {otherRecords.length > 0 && (
                        <span className="lead-calc-comp-records-toggle-aux">
                          (+ {otherRecords.length} excluded)
                        </span>
                      )}
                    </button>
                    {recordsExpanded && (
                      <div className="lead-calc-comp-records-table">
                        <div className="lead-calc-comp-records-row lead-calc-comp-records-row--head">
                          <span>Address</span>
                          <span>Sold</span>
                          <span>Price</span>
                          <span>$/sqft</span>
                          <span>Sqft</span>
                          <span>Bd/Yr</span>
                          <span>Band</span>
                        </div>
                        {usableRecords.map((c) => (
                          <div key={c.comp_signature} className="lead-calc-comp-records-row">
                            <span className="comp-cell-addr">{c.address?.street}, {c.address?.city}</span>
                            <span>{c.sold_date ? String(c.sold_date).slice(0, 10) : '—'}</span>
                            <span>{c.sold_price != null ? formatCurrency(c.sold_price) : '—'}</span>
                            <span>{c.ppsf != null ? `$${Number(c.ppsf).toFixed(0)}` : '—'}</span>
                            <span>{c.sqft != null ? Number(c.sqft).toLocaleString() : '—'}</span>
                            <span>{c.beds ?? '—'}bd / {c.year_built ?? '—'}</span>
                            <span className={`comp-cell-band band-${c.tolerance_band || 'tight'}`}>
                              {c.tolerance_band || 'tight'}
                            </span>
                          </div>
                        ))}
                        {otherRecords.length > 0 && (
                          <div className="lead-calc-comp-records-excluded">
                            Excluded: {otherRecords.map((c) => `${c.address?.street} (${c.exclude_reason || '—'})`).join('; ')}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {/* Tri-engine model-disagreement panel — surfaces when divergence > 5% */}
            {lead?.arv_model_conflict === true && lead?.arv_model_outputs && (
              <div className="lead-calc-arv-conflict">
                <div className="lead-calc-arv-conflict-header">
                  <span className="lead-calc-arv-conflict-icon">⚠</span>
                  <strong>ARV models disagree</strong>
                  {typeof lead.arv_model_outputs.divergence_pct === 'number' && (
                    <span className="lead-calc-arv-conflict-divergence">
                      {(lead.arv_model_outputs.divergence_pct * 100).toFixed(1)}% spread
                    </span>
                  )}
                </div>
              <div className="lead-calc-arv-conflict-models">
                {['claude', 'gpt', 'gemini'].map((p) => {
                  const m = lead.arv_model_outputs[p];
                  if (!m) return null;
                  const label = p === 'gpt' ? 'GPT-4o' : p === 'gemini' ? 'Gemini' : 'Claude';
                  return (
                    <span key={p} className={`lead-calc-arv-conflict-model ${m.ok ? '' : 'is-failed'}`}>
                      <span className="model-name">{label}</span>
                      {m.ok && m.estimated_arv != null
                        ? <span className="model-arv">{formatCurrency(m.estimated_arv)}</span>
                        : <span className="model-arv model-arv--failed">failed</span>}
                    </span>
                  );
                })}
              </div>
              <div className="lead-calc-arv-conflict-note">
                Committee result (median) is the displayed ARV. Verify with a comp pull before committing to MAO.
              </div>
            </div>
          )}
          </div>
          )}
        </div>
      </div>

      {/* Errors — full-width below the split, not crammed into a column */}
      {compAggregate?.error && (
        <div className="lead-calc-comp-aggregate-error">
          Comp pull failed: {compAggregate.error.slice(0, 100)}{compAggregate.error.length > 100 ? '…' : ''}
        </div>
      )}
      {compPullError && (
        <div className="lead-calc-arv-error">Comp pull failed: {compPullError}</div>
      )}
      {arvRefreshError && (
        <div className="lead-calc-arv-error">ARV refresh failed: {arvRefreshError}</div>
      )}

      {/* Shared line-item assumptions + three offer-point columns */}
      <div className="lead-calc-outputs">
        <div className="lead-calc-assumptions">
          <div className="lead-calc-assumption">
            <label className="lead-calc-assumption-label">Commissions</label>
            <div className="lead-calc-assumption-input-wrap">
              <input type="number" step="0.5" min="0" className="lead-calc-assumption-input"
                value={pctInput(commissionPct)} onChange={(e) => setCommissionPct(pctParse(e.target.value))} />
              <span className="lead-calc-assumption-unit">% of ARV</span>
            </div>
          </div>
          <div className="lead-calc-assumption">
            <label className="lead-calc-assumption-label">Closing</label>
            <div className="lead-calc-assumption-input-wrap">
              <input type="number" step="0.5" min="0" className="lead-calc-assumption-input"
                value={pctInput(closingPct)} onChange={(e) => setClosingPct(pctParse(e.target.value))} />
              <span className="lead-calc-assumption-unit">% of ARV</span>
            </div>
          </div>
          <div className="lead-calc-assumption">
            <label className="lead-calc-assumption-label">Investor profit</label>
            <div className="lead-calc-assumption-input-wrap">
              <input type="number" step="0.5" min="0" className="lead-calc-assumption-input"
                value={pctInput(profitPct)} onChange={(e) => setProfitPct(pctParse(e.target.value))} />
              <span className="lead-calc-assumption-unit">% of ARV</span>
            </div>
          </div>
          <button type="button" className="lead-calc-reset" onClick={resetToDefaults}>
            Reset to defaults
          </button>
        </div>

        <div className="lead-calc-output-grid">
          {(['conservative', 'target', 'walk_away']).map((key) => {
            const label = key === 'walk_away' ? 'Walk-away' : (key.charAt(0).toUpperCase() + key.slice(1));
            const fee = fees[key];
            const bd = computeBreakdown(arvNumber, { commissionPct, closingPct, profitPct, rehab, fee });
            return (
              <div key={key} className={`lead-calc-output ${key === 'target' ? 'is-target' : ''}`}>
                <div className="lead-calc-output-label">{label}</div>
                <div className="lead-calc-fee-edit">
                  <span className="lead-calc-fee-label">Assignment fee</span>
                  <input
                    type="number"
                    className="lead-calc-fee-input"
                    min="0"
                    value={fee ?? ''}
                    onChange={(e) => onFeeChange(key, e.target.value)}
                    aria-label={`${label} assignment fee`}
                  />
                </div>
                {bd && (
                  <div className="lead-calc-breakdown">
                    <div className="lead-calc-breakdown-row"><span>ARV</span><span>{formatCurrency(bd.arv)}</span></div>
                    <div className="lead-calc-breakdown-row"><span>− Commissions ({pctLabel(commissionPct)})</span><span>−{formatCurrency(bd.commissions)}</span></div>
                    <div className="lead-calc-breakdown-row"><span>− Closing ({pctLabel(closingPct)})</span><span>−{formatCurrency(bd.closing)}</span></div>
                    <div className="lead-calc-breakdown-row"><span>− Investor profit ({pctLabel(profitPct)})</span><span>−{formatCurrency(bd.profit)}</span></div>
                    <div className="lead-calc-breakdown-row"><span>− Rehab</span><span>−{formatCurrency(bd.rehab)}</span></div>
                    <div className="lead-calc-breakdown-row"><span>− Assignment fee</span><span>−{formatCurrency(bd.fee)}</span></div>
                  </div>
                )}
                <div className="lead-calc-mao">
                  {bd ? formatCurrency(bd.mao) : <span className="lead-calc-mao-empty">—</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {saveError && <div className="lead-detail-error">{saveError}</div>}
    </section>
  );
}
